import { ComponentRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SVG, G, Rect, Text as SvgText } from '@svgdotjs/svg.js';
import { ColorPaletteService
       } from 'app/controls/color-chooser/color-palette.service';
import { ClickInfo } from 'app/controls/image-cropper/drag-sensor';
import { determineFeedbackMessage, EditingAction
       } from 'app/controls/text-editing/text-editing.messages';
import { ToastComponent } from 'app/controls/toast/toast.component';
import { TransformationConstraints, TransformHandle,
       } from 'app/controls/transform-handle/transform-handle';
import { GalleryService } from 'app/gallery/gallery.service';
import { TextConstants } from 'app/text/text.constants';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { FontInjectorService } from 'collage-designer/font-injector.service';
import { GlobalConstants, StageConstants } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { normalizedOptions, TextApplianceResult, TextController,
         TextEditingResult
       } from '../../../text/text.controller';
import { TextControllerMethods, TextEditingRefusalReason
       } from '../../../text/text.controller.methods';
import { ZoomModeFit, zoomModeFitWith
       } from '../../../text/text.controller.zoom-mode-fit';
import { ZoomModeLimit } from '../../../text/text.controller.zoom-mode-limit';
import { largestFittingFontSize, laidOutInArea, withoutAutomaticLineBreaks
       } from '../../../text/text.layout';
import { normalizedFontFamily, plaintextToChunks, scaleModel,
         Text, TextArea, TextChunk, TextChunkKind, withoutFontDescription
       } from '../../../text/text.model';
import { applyModel, extractChunksFromNode, extractModel
       } from '../../../text/text.view.svg';
import { DisplayedText, plaintextOf, SvgTextInput, textInputApplianceOf
       } from '../../../text/text.view.svg.input';
import { CopyPlaceConfig } from '../../collage-designer-config';
import { CopyRules } from '../../copy-rules';
import { DesignStepService } from '../../design-step/design-step.service';
import { ActionBarComponent } from './action-bar/action-bar.component';
import { ActionBarDirective } from './action-bar/action-bar.directive';
import { Placeholder, PlaceholderType } from './placeholder';
import { PlaceholderContentService } from './placeholder-content.service';
import { PlaceholderService } from './placeholder.service';
import { SafeArea } from './safe-area';

class ZoomModeError extends TrivialError
{
  // i18n.error.zoomModeError.title
  // i18n.error.zoomModeError.message
  protected _name: string = 'ZoomModeError';
}

export type StackPlacement = 'above' | 'below';

interface TextInformation {
  id: string;
  text: Array<TextChunk>;
  isUnchanged: boolean;
}

export class TextPlaceholder extends Placeholder
{
  public declare group: G;
  private currentFontSizeTranslationFactor: number;
  private minFontSize: number;
  private rect: Rect;
  private readonly textListeners: Object = {
    'click': this.clickEvent.bind(this),
  };
  private readonly wrapperListeners: Object = {
    'mouseover': this.mouseOverText.bind(this),
    'mouseleave': this.mouseLeave.bind(this)
  };
  private actionBar: ActionBarComponent | undefined = undefined;
  private transformHandle: TransformHandle | undefined = undefined;
  private textInput: SvgTextInput | undefined = undefined;
  private isClickedWhileFocused: boolean = false;
  private readonly typingListener: (event: KeyboardEvent) => void =
      this.respondToTyping.bind(this);
  private toast: ComponentRef<ToastComponent> | undefined = undefined;
  private readonly safeArea: SafeArea;
  private readonly zoomModeFitOfArea: TextController = zoomModeFitWith(() => ({
    useAvailableArea: !this.fontSizeChangeable,
    allowsResizeTo: this.growsWithText ? this.fitsSafeArea : undefined,
  }));
  private cachedMaximalFontSize:
      { key: string; value: number } | undefined = undefined;

  constructor(
      errorService: ErrorService,
      loggingService: LoggingService,
      designStepService: DesignStepService,
      galleryService: GalleryService,
      private fontInjectorService: FontInjectorService,
      private colorPaletteService: ColorPaletteService,
      toolboxService: ToolboxService,
      placeholderContentService: PlaceholderContentService,
      placeholderService: PlaceholderService,
      eventTrackingService: EventTrackingService,
      productService: ProductService,
      private actionBarContainer: ActionBarDirective,
      text: SvgText,
      partId: string,
      dialog: MatDialog)
  {
    super(
        errorService, loggingService, designStepService, galleryService,
        toolboxService, placeholderContentService, placeholderService,
        eventTrackingService, productService, text, partId,
        PlaceholderType.Text, false, dialog);
    this.rect = this.wrapper.find('rect').at(0) as Rect;
    this.safeArea = new SafeArea(this.wrapper);
  }

  public async setup(clickAfterModelSet: boolean): Promise<void>
  {
    if (this.isContentChangeable()) {
      this.applyChangeableEventListeners();
    }

    this.set(StageConstants.ATTRIBUTE_PENDING, true);

    this.currentFontSizeTranslationFactor =
        await this.getFontSizeTranslationFactor();

    const attributeMinFontSize =
        Number(this.get(StageConstants.ATTRIBUTE_MIN_FONT_SIZE)) || 0;

    this.minFontSize = attributeMinFontSize === 0
        ? Math.round(this.toTemplateFontSize(TextConstants.MIN_FONT_SIZE))
        : attributeMinFontSize;
    this.set(StageConstants.ATTRIBUTE_MIN_FONT_SIZE, this.minFontSize);

    this.rect.css('fill', 'transparent');

    this.element.css('user-select', 'none');

    await this.fontInjectorService.waitForCompleteFontList();

    this.initTextModel('setup');
    const model = this.scaledDownToAvailableArea(this.getTextModel());

    await this.setTextModel(model);
    this.colorPaletteService.addColors([model.color]);
    this.set(StageConstants.ATTRIBUTE_PENDING, undefined);

    if (this.isLinked()) {
      await this.createLinkedText();
    }

    await this.initZoomMode();
    if (clickAfterModelSet) {
      await this.clickEvent();
    }
  }

  public async destructor(): Promise<void>
  {
    await super.destructor();
    if (this.isContentChangeable()) {
      this.removeChangeableEventListeners();
    }
    if (this.toast) {
      this.toast.destroy();
      this.toast = undefined;
    }
    this.safeArea.applyTouchedSidesHint(false);
  }

  public getTextModel(): Text
  {
    if (this.get(StageConstants.ATTRIBUTE_TEXT_MODEL) === undefined) {
      this.initTextModel('getTextModel');
    }

    const model = JSON.parse(this.get(StageConstants.ATTRIBUTE_TEXT_MODEL));
    model.fontDescription = this.fontInjectorService.getFont(
        model.fontName, !!model.fontBold, !!model.fontItalic);

    return scaleModel(model, this.currentFontSizeTranslationFactor);
  }

  public getPlaintext(): string
  {
    return this.getTextModel().text
      .map(chunk => chunk.kind === TextChunkKind.Whitespace
          ? ' '
          : chunk.kind === TextChunkKind.Word ? chunk.content || '' : '')
      .join('');
  }

  public getFontFamily(): string
  {
    return normalizedFontFamily(this.getTextModel().fontName);
  }

  private initTextModel(source: string): void
  {
    let placeholderTextModel = {};
    if (this.get(StageConstants.ATTRIBUTE_TEXT_MODEL) === undefined) {
      const placeholderText = this.getPlaceholderText();
      placeholderTextModel = (
        (placeholderText &&
         [GlobalConstants.MODE_GOD, GlobalConstants.MODE_TRANSLATOR]
            .indexOf(Globals.mode) === -1 &&
         this.isUnchanged())
          ? {'text': placeholderText}
          : {});
    }
    else {
      try {
        placeholderTextModel = JSON.parse(
            this.get(StageConstants.ATTRIBUTE_TEXT_MODEL));
      }
      catch (e) {
        this.loggingService.log(
            'TextPlaceholder',
            Severity.ERROR,
            '[ALERT] broken TextModel detected: ' +
            this.get(StageConstants.ATTRIBUTE_TEXT_MODEL) +
            ' source: ' + source +
            ' error: ' + e.toString());
        this.set(StageConstants.ATTRIBUTE_TEXT_MODEL, undefined);
        return this.initTextModel(source + '-retry');
      }
    }

    this.set(StageConstants.ATTRIBUTE_TEXT_MODEL, JSON.stringify(
        {...withoutFontDescription(extractModel(this.wrapper)),
         ...this.defaultTextModel,
         ...placeholderTextModel,
    }));
  }

  private getPlaceholderText(): Array<TextChunk> | undefined
  {
    const plaintext = this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_TEXT);

    // XML Attributes can't contain newlines. So we treat a literal '\n' as a
    // newline
    return (
      plaintext === undefined
        ? undefined
        : plaintextToChunks(plaintext.replace(/\\n/g, '\n')));
  }

  public async setTextModel(model: Text): Promise<void>
  {
    const scaledModel = scaleModel(
      model, 1 / this.currentFontSizeTranslationFactor);

    if (this.growsWithText) {
      this.applyAreaAroundCenter(scaledModel.availableArea);
      this.safeArea.slideWrapperInto();
      if (this.hasFocus()) {
        this.safeArea.applyTouchedSidesHint(true);
      }
    }

    this.set(
      StageConstants.ATTRIBUTE_TEXT_MODEL,
      JSON.stringify(withoutFontDescription(scaledModel)));

    await this.displayModel(scaledModel);

    if (this.transformHandle) {
      this.transformHandle.refresh();
    }
    if (this.actionBar) {
      this.actionBar.setTargetRegion();
      this.actionBar.updateFittingSide();
    }
    this.redrawMarker();
    this.applyTextInputCursor();
  }

  private scaledDownToAvailableArea(model: Text): Text
  {
    const fitted = ZoomModeFit.setText(
        model, model.text, { minFontSize: this.minFontSizePt });

    return fitted.type === TextApplianceResult.Success ? fitted.model : model;
  }

  private applyAreaAroundCenter(area: TextArea): void
  {
    const centerX = this.wrapper.cx();
    const centerY = this.wrapper.cy();

    this.wrapper
      .size(area.width, area.height)
      .move(centerX - area.width / 2, centerY - area.height / 2);
  }

  private get fitsSafeArea(): (area: TextArea) => boolean
  {
    return this.safeArea.holdsWrapperOfSize(
        this.currentFontSizeTranslationFactor);
  }

  private notifyRefusal(message: string | undefined): void
  {
    if (!message) {
      return;
    }
    if (!this.toast) {
      this.toast = this.actionBarContainer.createToast();
    }
    this.toast.instance.show(message);
  }

  private get defaultTextModel(): Text
  {
    return JSON.parse(
      this.get(StageConstants.ATTRIBUTE_DEFAULT_TEXT_MODEL) || '{}');
  }

  private async displayModel(model: Text): Promise<void>
  {
    await this.fontInjectorService.waitForCompleteFontList();

    try {
      await this.fontInjectorService.addModelFont(model, 60);
    } catch (e) {
      await this.fontInjectorService.buildFontLoadingError(
          e, model.fontName, true);
    }

    applyModel(
      this.wrapper,
      model,
      (await this.fontInjectorService.getFontDescription(model)));
    this.applyFocusBackground();
  }

  protected async focus(): Promise<void>
  {
    await super.focus();
    this.applyFocusBackground();
  }

  private applyFocusBackground(): void
  {
    const className = StageConstants.CLASS_NAME_TEXT_FOCUS_BACKGROUND;
    const previous = this.wrapper.findOne('.' + className);
    if (previous) {
      previous.remove();
    }

    const bgColor = this.getTextModel().bgColor;
    const hasBackground = !!bgColor && bgColor !== 'transparent';
    if (!this.hasFocus() || hasBackground) {
      return;
    }

    const wrapperToScreen =
        (this.wrapper.node as SVGGraphicsElement).getScreenCTM();
    const radius = StageConstants.TEXT_MARKER_SCREEN_RADIUS /
        Math.hypot(wrapperToScreen.a, wrapperToScreen.b);

    this.element.before(new Rect()
      .addClass(className)
      .size('100%', '100%')
      .radius(radius)
      .attr({
        'fill': 'black',
        'fill-opacity': 0.2,
        'pointer-events': 'none',
      }));
  }

  public suspendPointerEvents(): void
  {
    this.wrapper.node.style['pointer-events'] = 'none';
  }

  public resumePointerEvents(): void
  {
    this.wrapper.node.style['pointer-events'] = 'auto';
  }

  private get growsWithText(): boolean
  {
    return this.isFlexible() &&
        this.zoomMode === StageConstants.ZOOM_MODE_FIT;
  }

  public isFlexible(): boolean
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    return this.designStepService
      .getDesignStepConfig().textPlaceholdersSettings.showCreateNewButton;
  }

  public hasEditableContent(): boolean
  {
    return true;
  }

  private async getFontSizeTranslationFactor(): Promise<number>
  {
    const part = this.designStepService.getPart();
    const template = await this.designStepService.getTemplate();
    const docBox = StageUtils.originalDimensions(this.element.root());

    const rotated = template.stageConfig.rotation !== 0;

    const templateUnitsHeight = docBox.height;
    const measuredHeight = rotated ? part.measuredWidth : part.measuredHeight;

    const millimetersPerTemplateUnit = measuredHeight / templateUnitsHeight;
    const inchesPerMillimeter = 1 / 25.4;
    const pointsPerInch = 72;

    const pointsPerTemplateUnit = millimetersPerTemplateUnit *
        inchesPerMillimeter *
        pointsPerInch;

    return pointsPerTemplateUnit;
  }

  public toFontSizePt(templateFontSize: number): number
  {
    return templateFontSize * this.currentFontSizeTranslationFactor;
  }

  public toTemplateFontSize(fontSizePt: number): number
  {
    return fontSizePt / this.currentFontSizeTranslationFactor;
  }

  public get minFontSizePt(): number
  {
    return Math.round(this.toFontSizePt(this.minFontSize));
  }

  public get maxFontSizePt(): number
  {
    const fittingReferenceFontSize = 100;
    const hardLimit = normalizedOptions({}).maxFontSize;
    const model = this.getTextModel();

    const hasVisibleText =
        model.text.some(chunk => chunk.kind !== TextChunkKind.Control);

    if (!model.fontDescription || !hasVisibleText) {
      return hardLimit;
    }

    if (this.growsWithText) {
      return this.maximalFontSizeInSafeArea(model);
    }

    const usage = TextControllerMethods.getTextUsageArea(
        {...model, fontSize: fittingReferenceFontSize});

    if (!(usage.width > 0 && usage.height > 0)) {
      return hardLimit;
    }

    const fitting = fittingReferenceFontSize * Math.min(
        model.availableArea.width / usage.width,
        model.availableArea.height / usage.height);

    return Math.max(
        this.minFontSizePt,
        Math.min(hardLimit, Math.floor(fitting)));
  }

  private maximalFontSizeInSafeArea(model: Text): number
  {
    const key = this.get(StageConstants.ATTRIBUTE_TEXT_MODEL) + '|' +
        this.wrapper.parent().attr('transform');

    const cached = this.cachedMaximalFontSize;
    if (!cached || cached.key !== key) {
      this.cachedMaximalFontSize =
          { key, value: largestFittingFontSize(model, this.fitsSafeArea) };
    }

    return Math.max(
        this.minFontSizePt, this.cachedMaximalFontSize.value);
  }

  private applyChangeableEventListeners(): void
  {
    const text = this.element.node;

    const observer = new MutationObserver(mutations => {
      this.changed.emit(this.element.id());
    });
    observer.observe(text, {attributes: true});

    for (const listener in this.textListeners) {
      if (this.textListeners.hasOwnProperty(listener)) {
        text.addEventListener(listener, this.textListeners[listener]);
        this.rect.node.addEventListener(
            listener, this.textListeners[listener]);
      }
    }
    for (const listener in this.wrapperListeners) {
      if (this.wrapperListeners.hasOwnProperty(listener)) {
        this.wrapper.node.addEventListener(
            listener, this.wrapperListeners[listener]);
      }
    }
  }

  private removeChangeableEventListeners(): void
  {
    const text = this.element.node;

    for (const listener in this.textListeners) {
      if (this.textListeners.hasOwnProperty(listener)) {
        text.removeEventListener(listener, this.textListeners[listener]);
        this.rect.node.removeEventListener(
            listener, this.textListeners[listener]);
      }
    }
    for (const listener in this.wrapperListeners) {
      if (this.wrapperListeners.hasOwnProperty(listener)) {
        this.wrapper.node.removeEventListener(
            listener, this.wrapperListeners[listener]);
      }
    }
  }

  private async mouseOverText(event: Event): Promise<void>
  {
    if (Utils.isLargeDevice()) {
      await super.mouseOver();
      if (this.galleryService.getDraggingImageId() === undefined &&
          !this.placeholderService.hasTransformHandle()) {
        if (this.isFlexible()) {
          this.wrapper.node.style.cursor = 'pointer';
        }
        else {
          this.wrapper.node.style.cursor = 'text';
        }
      }
    }
  }

  private async createLinkedText(): Promise<void>
  {
    this.detachContent();

    const currentPartId = this.designStepService.getPartId();
    const copyFrom = this.get(StageConstants.ATTRIBUTE_COPY_FROM);

    if (! copyFrom) {
      return;
    }

    let link = undefined;

    try {
      link = CopyRules.decodePlace(copyFrom, currentPartId);
    }
    catch (e) {
      this.errorService.add(e, 'PlaceholderText');
      await this.errorService.flush('PlaceholderText');
    }

    if (link.part === currentPartId) {
      const rootNode = this.element.node.getRootNode();
      const superiorText = rootNode.getElementById(link.item) as SvgText;
      if (! superiorText) {
        return;
      }
      const observer = new MutationObserver(async mutations => {
        await this.copyTextFrom(superiorText);
      });
      observer.observe(superiorText.node, {
          attributes: true,
          attributeOldValue: true
      });

      await this.copyTextFrom(superiorText);
    }
    else {
      await this.adoptItemFromAnotherPart(link);
    }
  }

  private async adoptItemFromAnotherPart(link: CopyPlaceConfig): Promise<void>
  {
    const structure =
        await this.designStepService.getSVGStructureByPartId(link.part);
    if (!structure) {
      return;
    }
    const doc = (new DOMParser()).parseFromString(structure, 'image/svg+xml');

    const superiorText = SVG(doc.getElementById(link.item)) as SvgText;
    if (!superiorText) {
      return;
    }

    await this.copyTextFrom(superiorText);
  }

  private async copyTextFrom(superiorText: SvgText): Promise<void>
  {
    await this.setTextValidated(extractChunksFromNode(superiorText));
  }

  public get fontChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_FONT_CHANGEABLE) !== 'false';
  }

  public get fontSizeChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_FONT_SIZE_CHANGEABLE) !== 'false';
  }

  public get colorChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_COLOR_CHANGEABLE) !== 'false';
  }

  public get alignmentChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_ALIGNMENT_CHANGEABLE) !== 'false';
  }

  public async extractInformation(): Promise<TextInformation>
  {
    return {
      id: this.get('id'),
      text: this.getTextModel().text,
      isUnchanged: this.isUnchanged(),
    };
  }

  public async applyInformation(info: TextInformation): Promise<void>
  {
    await this.setTextValidated(info.text);
  }

  public get zoomModeTextController(): TextController | undefined
  {
    switch (this.zoomMode) {
      case 'fit':
        return this.zoomModeFitOfArea;
      case 'limit':
        return ZoomModeLimit;
      default:
        return undefined;
    }
  }

  private async initZoomMode(): Promise<void>
  {
    await this.setFontValidated();
    await this.setTextValidated();
  }

  private async setFontValidated(): Promise<void>
  {
    const model = this.getTextModel();
    const validated = this.zoomModeTextController.setFont(
        model,
        model.fontDescription,
        { minFontSize: this.minFontSizePt });
    if (validated.type !== TextApplianceResult.Success) {
      if (this.isLinked()) {
        this.loggingService.log(
            'TextPlaceholder',
            Severity.WARNING,
            'Possible broken template ' +
            '"' + this.designStepService.getTemplateId() + '" detected. ' +
            'Linked placeholder "' + this.wrapper.attr('id') + '" can\'t ' +
            'apply font family.');
      }
      else {
        this.errorService.add(new ZoomModeError(), 'TextPlaceholder');
        await this.errorService.flush('TextPlaceholder');
      }
      return;
    }

    this.setTextModel(validated.model);
  }

  public async setTextValidated(newText?: Array<TextChunk>): Promise<void>
  {
    const zoomMode = this.zoomModeTextController;

    if (!zoomMode) {
      this.errorService.add(new ZoomModeError(), 'TextPlaceholder');
      await this.errorService.flush('TextPlaceholder');
      return;
    }

    const model = this.getTextModel();
    const validated = zoomMode.setText(
      model,
      newText || model.text,
      { minFontSize: this.minFontSizePt }
    );

    if (validated.type !== TextApplianceResult.Success) {
      if (this.isLinked()) {
        this.loggingService.log(
            'TextPlaceholder',
            Severity.WARNING,
            'Possible broken template ' +
            '"' + this.designStepService.getTemplateId() + '" detected. ' +
            'Linked placeholder "' + this.wrapper.attr('id') + '" is ' +
            'too small.');
      }
      else {
        this.errorService.add(new ZoomModeError(), 'TextPlaceholder');
        await this.errorService.flush('TextPlaceholder');
      }
      return;
    }

    this.setTextModel(validated.model);
  }

  private getMinSize(): TransformationConstraints
  {
    const model = this.getTextModel();
    const narrowest = (this.growsWithText && laidOutInArea(
        {...model, availableArea: { width: 0, height: 0 }},
        this.fitsSafeArea)) || model;
    const usage = TextControllerMethods.getTextUsageArea(narrowest);

    return {
      minWidth: usage.width / this.currentFontSizeTranslationFactor,
      minHeight: usage.height / this.currentFontSizeTranslationFactor,
    };
  }

  protected async afterTransformationDone(): Promise<void>
  {
    this.safeArea.applyTouchedSidesHint(false);
    if (this.actionBar) {
      this.actionBar.updateFittingSide();
    }
    const zoomMode = this.zoomModeTextController;
    const textModel = this.getTextModel();
    const availableArea = {
      width: this.wrapper.width() as number *
          this.currentFontSizeTranslationFactor,
      height: this.wrapper.height() as number *
          this.currentFontSizeTranslationFactor
    };
    const result = zoomMode.setAvailableArea(textModel, availableArea);
    if (result.type === TextApplianceResult.Success) {
      this.setTextModel(result.model);
    }
    else {
      this.loggingService.log(
          'TextPlaceholder',
          Severity.ERROR,
          '[ALERT] TransformationError occured: ' +
          'template="' + this.designStepService.getTemplateId() + '" ' +
          'placeholder="' + this.wrapper.attr('id') + '" ' +
          'availableArea=' + JSON.stringify(availableArea) + ' ' +
          'textUsageArea=' + JSON.stringify(
              TextControllerMethods.getTextUsageArea(textModel)) + ' ' +
          'textModel=' + JSON.stringify(withoutFontDescription(textModel)));
    }

    if (this.textInput) {
      this.textInput.focus();
    }
  }

  public async clickEvent(event?: MouseEvent | ClickInfo): Promise<void>
  {
    this.isClickedWhileFocused = this.hasFocus();
    await super.clickEvent(event);
  }

  protected async handleClick(click?: ClickInfo): Promise<void>
  {
    if (click && this.isClickedWhileFocused && !this.textInput) {
      this.enterTextEditing(click);
    }

    document.addEventListener('keydown', this.typingListener);
    this.toolboxService.open('TextToolbox');
    await this.injectActionBar();
  }

  private respondToTyping(event: KeyboardEvent): void
  {
    const target = event.composedPath()[0] as HTMLElement;
    const isTypingElsewhere = target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);

    if (this.textInput || isTypingElsewhere || event.key.length !== 1 ||
        event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    this.enterTextEditing();
  }

  public mouseLeave(event: MouseEvent): void
  {
    event.stopPropagation();
    if (this.actionBar &&
        this.actionBar.nativeElement.contains(event.relatedTarget as Node)) {
      return;
    }

    super.mouseLeave(event);
  }

  private async setTransformHandle(): Promise<void>
  {
    if (!this.hasTransformHandle()) {
      this.transformHandle = new TransformHandle(
          this.wrapper,
          this.group,
          () => this.getMinSize(),
          this.whileTransformation.bind(this),
          await this.afterTransformationDone.bind(this),
          (click: ClickInfo) => this.clickEvent(click),
          this.growsWithText
            ? {
                isSatisfied: () => this.safeArea.containsWrapper,
                slideIntoAllowedArea: () => this.safeArea.slideWrapperInto(),
                pullWidthIntoAllowedArea: () =>
                    this.safeArea.pullWrapperWidthInto(),
              }
            : undefined);

      StageUtils.ensureCorrectLayerPositions(this.element.root());
    }
  }

  private whileTransformation(): void
  {
    const model = JSON.parse(this.get(StageConstants.ATTRIBUTE_TEXT_MODEL));
    const font = this.fontInjectorService.getFont(
        model.fontName, !!model.fontBold, !!model.fontItalic);
    applyModel(
        this.wrapper,
        this.growsWithText ? this.laidOutToWrapperWidth() : model,
        font);
    if (this.growsWithText) {
      this.safeArea.applyTouchedSidesHint(true);
    }
    this.applyTextInputCursor();
    if (this.actionBar) {
      this.actionBar.setTargetRegion();
    }
    this.redrawMarker();
  }

  private laidOutToWrapperWidth(): Text
  {
    const factor = this.currentFontSizeTranslationFactor;
    const model = this.getTextModel();
    const laidOutModel = laidOutInArea({
      ...model,
      availableArea: {
        width: (this.wrapper.width() as number) * factor,
        height: (this.wrapper.height() as number) * factor,
      }
    }, this.fitsSafeArea) || model;

    const scaledModel = scaleModel(laidOutModel, 1 / factor);
    this.applyAreaAroundCenter(scaledModel.availableArea);

    return scaledModel;
  }

  private removeTransformHandle(): void
  {
    if (this.hasTransformHandle()) {
      this.transformHandle.teardown();
      this.transformHandle = undefined;
      StageUtils.ensureCorrectLayerPositions(this.element.root());
    }
  }

  public attachRotationControl(control: HTMLElement | undefined): void
  {
    if (this.transformHandle) {
      this.transformHandle.attachRotationControl(control);
    }
  }

  protected determineGroup(): G
  {
    const parent = this.wrapper.parent();
    if (parent?.node.tagName === 'g' && parent.node.childElementCount === 1) {
      return parent as G;
    }

    const group = new G();
    this.wrapper.before(group);
    group.add(this.wrapper);
    return group;
  }

  public restack(reference: TextPlaceholder, placement: StackPlacement): void
  {
    this.withoutMarker(() => reference.withoutMarker(() => {
      if (placement === 'above') {
        this.group.insertAfter(reference.group);
      }
      else {
        this.group.insertBefore(reference.group);
      }
    }));

    StageUtils.ensureCorrectLayerPositions(this.element.root());
    this.changed.emit(this.element.id());
  }

  public hasTransformHandle(): boolean
  {
    return !!this.transformHandle;
  }

  private async injectActionBar(): Promise<void>
  {
    if (this.isFlexible()) {
      await this.setTransformHandle();
    }

    if (!this.actionBar) {
      this.actionBar = this.actionBarContainer.createComponent();
      this.actionBar.initialize(this);
    }
  }

  private removeActionBar(): void
  {
    if (this.actionBar) {
      this.actionBar.destroy();
      this.actionBar = undefined;
    }
  }

  public enterTextEditing(click?: ClickInfo): void
  {
    if (!this.isContentChangeable() || this.isPending()) {
      return;
    }

    if (!this.textInput) {
      const surfaces = this.transformHandle
        ? [this.wrapper.node, this.transformHandle.movingControlNode]
        : [this.wrapper.node];

      this.textInput = new SvgTextInput(
          this.wrapper,
          this.element,
          surfaces,
          {
            displayedModel: () => this.displayedModel(),
            respondToInput: () => this.respondToTextInput(),
            respondToEscape: () => this.leaveTextEditing(),
          },
          plaintextOf(withoutAutomaticLineBreaks(this.getTextModel().text)));
      this.textInput.applyInitialSelection(click, this.isUnchanged());

      this.wrapper.node.style.cursor = 'text';
      if (this.transformHandle) {
        this.transformHandle.applyMovingEnabled(false);
      }
      if (this.actionBar) {
        this.actionBar.updateFittingSide();
      }
    }

    this.textInput.focus();
  }

  public get isTextEditing(): boolean
  {
    return !!this.textInput;
  }

  public leaveTextEditing(): void
  {
    if (!this.textInput) {
      return;
    }

    this.textInput.teardown();
    this.textInput = undefined;
    this.wrapper.node.style.cursor = 'default';
    if (this.transformHandle) {
      this.transformHandle.applyMovingEnabled(true);
    }
    if (this.actionBar) {
      this.actionBar.updateFittingSide();
    }
  }

  private displayedModel(): DisplayedText | undefined
  {
    const model = JSON.parse(this.get(StageConstants.ATTRIBUTE_TEXT_MODEL));
    const font = this.fontInjectorService.getFont(
        model.fontName, !!model.fontBold, !!model.fontItalic);

    return font ? { model, font } : undefined;
  }

  private applyTextInputCursor(): void
  {
    if (this.textInput) {
      this.textInput.applyCursor();
    }
  }

  private textInputRefusalMessageOf(
    result: TextEditingResult): string | undefined
  {
    const cannotGrowFurther = this.growsWithText &&
        result.reason === TextEditingRefusalReason.TextTooHigh;

    return cannotGrowFurther
      ? 'i18n.control.textEditing.rejectMessage.maxCharactersReached'
      : determineFeedbackMessage(
          EditingAction.ChangeText,
          result,
          { flexible: this.isFlexible(), zoomMode: this.zoomMode });
  }

  private async respondToTextInput(): Promise<void>
  {
    const textInput = this.textInput;
    const model = this.getTextModel();
    const previousValue = plaintextOf(withoutAutomaticLineBreaks(model.text));

    const appliance = textInputApplianceOf(
        previousValue,
        textInput.value,
        value => this.zoomModeTextController.setText(
            model,
            plaintextToChunks(value),
            { minFontSize: this.minFontSizePt }));

    if (appliance.completeRefusal) {
      this.notifyRefusal(
          this.textInputRefusalMessageOf(appliance.completeRefusal));
    }
    if (appliance.partialResult.type !== TextApplianceResult.Success) {
      textInput.applyValue(previousValue, appliance.caret);
      return;
    }
    await this.setTextModel(appliance.partialResult.model);

    const appliedValue =
        plaintextOf(withoutAutomaticLineBreaks(this.getTextModel().text));
    if (textInput.value !== appliedValue) {
      textInput.applyValue(
          appliedValue, Math.min(appliedValue.length, appliance.caret));
    }

    await this.commitContent();
  }

  public blur(): void
  {
    super.blur();
    this.applyFocusBackground();
    this.safeArea.applyTouchedSidesHint(false);
    document.removeEventListener('keydown', this.typingListener);
    this.leaveTextEditing();
    this.removeActionBar();
    this.removeTransformHandle();

  }
}
